
import React, { useRef, useState, useEffect } from "react";
import { Table, Button, Modal } from "@themesberg/react-bootstrap";
import PaginationComponent from "../../../../components/Pagination";
import Toggle from "./Toggle";
import ActionMenu from "../../TransactionsBanking/components/ActionMenu";
import { isDateValid } from "../constants";
import TableErrorMessage from '../../../../components/TableErrorMessage';
import useCheckPermission from "../../../../utils/checkPermission";
import { toast } from "../../../../components/Toast";
import { DeleteConfirmationModal } from "../../../../components/ConfirmationModal";

const PromoCodesTable = ({
    codes,
    loading,
    visibleColumns,
    page,
    totalPages,
    setPage,
    limit,
    setLimit,
    headers,
    handleCreateEdit,
    handleDelete,
    handleToggleStatus,
    handleToggleRedeemed,
    type,
    // For delete confirmation within the table if needed, or passed down
    onHandleDeleteConfirmation,
    selectedpromoCode,
    deletemodal,
    setDeleteModal,
    isVoucherTab = false,
    totalCount,
    showTotalCount = false,
    countText = "Total Promo Code"
}) => {
    // Get the ID field name based on the tab type
    const getCodeId = (row) => {
        if (!row) return null;
        return isVoucherTab ? (row.voucherCodeId || row.promocodesId || row.id) : row.promocodesId;
    };
    const { isHidden } = useCheckPermission();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [showRedeemedConfirmModal, setShowRedeemedConfirmModal] = useState(false);
    const [pendingRedeemedChange, setPendingRedeemedChange] = useState(null);
    const headerRef = useRef(null); // Assuming headerRef is passed or we manage height here? 
    // Actually, in the original code, headerRef was used for calculation. 
    // Let's keep the resize logic here if it's specific to the table, or accept style props.
    // For simplicity, I'll copy the resize logic as it pertains to the table container.

    const [tableMaxHeight, setTableMaxHeight] = useState("");

    useEffect(() => {
        const updateMaxHeight = () => {
            // We might need to adjust these selectors or pass refs for dynamic calculation
            // For now, let's establish a reasonable default or keep the logic local if possible.
            // The original code relied on headerRef which was outside. 
            // Let's just use window height - offset for now to be safe, or 100% if we assume the parent handles layout.
            // But to strictly follow "same view/ui", I should try to preserve the behavior.
            // Ideally the parent should pass the height or we calculate it based on a known offset.

            // Let's stick to the current logic but adapt it. 
            // Since we don't have the headerRef of the parent page here, we might miss that height. 
            // Usage of `visibleColumns` suggests table structure is passed in.

            // Simplification: Let's assume the parent deals with layout or we just set a flex grow.
            // Original: window.innerHeight - totalHeight - 100

            // I'll leave the complex height calculation for now and focus on rendering the table content correctly. 
            // The original used: responsiveTable.style.maxHeight

            const responsiveTable = document.querySelector('.table-reusable-promo');
            if (responsiveTable) {
                const availableHeight = window.innerHeight - 300; // estimated offset
                if (window.innerWidth >= 1000) {
                    responsiveTable.style.maxHeight = `${availableHeight}px`;
                    responsiveTable.style.overflowY = 'auto';
                } else {
                    responsiveTable.style.maxHeight = '100%';
                    responsiveTable.style.overflowY = 'unset';
                }
            }
        };

        updateMaxHeight();
        window.addEventListener("resize", updateMaxHeight);
        return () => window.removeEventListener("resize", updateMaxHeight);
    }, [codes]);


    const handleStatusChangeClick = (codeId, newStatus, code, isCodeValid) => {
        if (!isCodeValid && type !== "Create") {
            return toast("Validity date has been expired", "error");
        }
        setPendingStatusChange({
            codeId,
            newStatus,
            code,
            isCodeValid
        });
        setShowConfirmModal(true);
    };

    const confirmStatusChange = () => {
        if (pendingStatusChange) {
            handleToggleStatus(
                pendingStatusChange.codeId,
                pendingStatusChange.newStatus
            );
        }
        setShowConfirmModal(false);
        setPendingStatusChange(null);
    };

    const cancelStatusChange = () => {
        setShowConfirmModal(false);
        setPendingStatusChange(null);
    };

    const handleRedeemedChangeClick = (codeId, newIsRedeemed, code) => {
        setPendingRedeemedChange({ codeId, newIsRedeemed, code });
        setShowRedeemedConfirmModal(true);
    };

    const confirmRedeemedChange = () => {
        if (pendingRedeemedChange && handleToggleRedeemed) {
            handleToggleRedeemed(pendingRedeemedChange.codeId, pendingRedeemedChange.newIsRedeemed);
        }
        setShowRedeemedConfirmModal(false);
        setPendingRedeemedChange(null);
    };

    const cancelRedeemedChange = () => {
        setShowRedeemedConfirmModal(false);
        setPendingRedeemedChange(null);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "NA";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <>
            {visibleColumns?.length > 0 ? (
                <>
                    <div className="TableBg mt-4">
                        <Table striped responsive hover size="sm" className="text-center table-reusable-promo">
                            <thead className="thead-light">
                                <tr>
                                    {visibleColumns.map((h, index) => (
                                        <th key={`${h.value}-${index}`}>{h.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {codes?.map((row) => {
                                    const codeId = getCodeId(row);

                                    // Handle different field names for promo vs voucher codes
                                    const code = row.code;
                                    // For vouchers, `amountUsd` is the USD amount and `amountTtd` holds the TTD amount
                                    const amount = isVoucherTab ? row.amountUsd : row.amount;
                                    const amountTtd = isVoucherTab ? row.amountTtd : undefined;
                                    const validity = isVoucherTab ? row.expiresAt : row.validity;
                                    // Voucher status is string ("active"/"inactive"), promo is boolean
                                    const status = isVoucherTab
                                        ? (row.status === "active" || row.status === true)
                                        : row.status;
                                    const isRedeemed = isVoucherTab ? row.isRedeemed : undefined;

                                    return (
                                        <tr key={codeId}>
                                            {visibleColumns.map((col) => {
                                                switch (col?.accessorKey) {
                                                    case "code":
                                                        return <td key={col.value}>{code || "NA"}</td>;

                                                    case "promocodesId":
                                                        return <td key={col.value}>{codeId || "NA"}</td>;

                                                    case "amount":
                                                        // For vouchers this is Amount (USD); for promo it's the original amount
                                                        return <td key={col.value}>${amount || "0"}</td>;

                                                    case "amountTtd":
                                                        // Voucher-only column for Amount (TTD) - use TT$ symbol
                                                        return (
                                                            <td key={col.value}>
                                                                {typeof amountTtd !== "undefined" && amountTtd !== null
                                                                    ? `TT$ ${amountTtd}`
                                                                    : "NA"}
                                                            </td>
                                                        );

                                                    case "validity":
                                                        return <td key={col.value}>{formatDate(validity)}</td>;

                                                    case "status":
                                                        return (
                                                            <td
                                                                key={col.value}
                                                                hidden={isHidden({
                                                                    module: { key: "PromoCode", value: "U" },
                                                                })}
                                                                style={{ color: status ? "green" : "red" }}
                                                                $                >
                                                                <Toggle
                                                                    checked={isDateValid(validity) ? status : false}
                                                                    onChange={(e) => {
                                                                        handleStatusChangeClick(
                                                                            codeId,
                                                                            e.target.checked,
                                                                            code,
                                                                            type !== "Create" && isDateValid(validity)
                                                                        );
                                                                    }}
                                                                    align="left"
                                                                    size="small"
                                                                />
                                                            </td>
                                                        );

                                                    case "isRedeemed":
                                                        if (!isVoucherTab) return null;
                                                        return (
                                                            <td
                                                                key={col.value}
                                                                hidden={isHidden({
                                                                    module: { key: "PromoCode", value: "U" },
                                                                })}
                                                            >
                                                                <Toggle
                                                                    checked={!!isRedeemed}
                                                                    onChange={(e) => {
                                                                        handleRedeemedChangeClick(
                                                                            codeId,
                                                                            e.target.checked,
                                                                            code
                                                                        );
                                                                    }}
                                                                    align="left"
                                                                    size="small"
                                                                />
                                                            </td>
                                                        );

                                                    case "action":
                                                        return (
                                                            <td
                                                                key={col.value}
                                                                hidden={
                                                                    isHidden({
                                                                        module: { key: "PromoCode", value: "U" },
                                                                    }) &&
                                                                    isHidden({ module: { key: "PromoCode", value: "D" } })
                                                                }
                                                            >
                                                                <ActionMenu
                                                                    handleCreateEdit={handleCreateEdit}
                                                                    handleDelete={onHandleDeleteConfirmation}
                                                                    data={
                                                                        isVoucherTab
                                                                            ? {
                                                                                voucherCodeId: codeId,
                                                                                promocodesId: codeId,
                                                                                code,
                                                                                amount: amount,
                                                                                amountTtd,
                                                                                validity,
                                                                                status,
                                                                                isRedeemed,
                                                                            }
                                                                            : {
                                                                                promocodesId: codeId,
                                                                                code,
                                                                                amount,
                                                                                validity,
                                                                                status,
                                                                            }
                                                                    }
                                                                />
                                                            </td>
                                                        );

                                                    default:
                                                        return null;
                                                }
                                            })}
                                        </tr>
                                    );
                                })}

                                {(!codes || codes.length === 0) && (
                                    <tr>
                                        <td colSpan={visibleColumns.length} className="text-danger text-center">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {codes?.length > 0 && !loading && (
                        <PaginationComponent
                            page={page}
                            totalPages={totalPages}
                            setPage={setPage}
                            limit={limit}
                            setLimit={setLimit}
                            totalCount={totalCount}
                            showTotalCount={showTotalCount}
                            countText={countText}

                        />
                    )}
                </>
            ) : (
                <TableErrorMessage />
            )}

            <Modal
                show={showConfirmModal}
                onHide={cancelStatusChange}
                centered
                backdrop="static"
                style={{
                    backdropFilter: "blur(15px)",
                    WebkitBackdropFilter: "blur(15px)",
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Status Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingStatusChange && (
                        <div>
                            <p>
                                Are you sure you want to{" "}
                                <strong>
                                    {pendingStatusChange.newStatus ? "activate" : "deactivate"}
                                </strong>{" "}
                                the {isVoucherTab ? 'voucher' : 'promo'} code <strong>"{pendingStatusChange.code}"</strong>?
                            </p>
                            <div className="d-flex align-items-center mt-3">
                                <strong>Status will change to: </strong>
                                <span
                                    className={`ms-2 badge ${pendingStatusChange.newStatus ? "bg-success" : "bg-danger"
                                        }`}
                                >
                                    {pendingStatusChange.newStatus ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant={pendingStatusChange?.newStatus ? "success" : "danger"}
                        onClick={confirmStatusChange}
                    >
                        Confirm{" "}
                        {pendingStatusChange?.newStatus ? "Activation" : "Deactivation"}
                    </Button>
                    <Button variant="secondary" onClick={cancelStatusChange}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showRedeemedConfirmModal}
                onHide={cancelRedeemedChange}
                centered
                backdrop="static"
                style={{
                    backdropFilter: "blur(15px)",
                    WebkitBackdropFilter: "blur(15px)",
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Redeemed Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingRedeemedChange && (
                        <div>
                            <p>
                                Are you sure you want to mark voucher code{" "}
                                <strong>"{pendingRedeemedChange.code}"</strong> as{" "}
                                <strong>
                                    {pendingRedeemedChange.newIsRedeemed ? "redeemed" : "not redeemed"}
                                </strong>?
                            </p>
                            <div className="d-flex align-items-center mt-3">
                                <strong>Redeemed will be: </strong>
                                <span
                                    className={`ms-2 badge ${pendingRedeemedChange.newIsRedeemed ? "bg-success" : "bg-secondary"}`}
                                >
                                    {pendingRedeemedChange.newIsRedeemed ? "Redeemed" : "Not Redeemed"}
                                </span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant={pendingRedeemedChange?.newIsRedeemed ? "success" : "danger"}
                        onClick={confirmRedeemedChange}
                    >
                        Confirm{" "}
                        {pendingRedeemedChange?.newIsRedeemed ? "Redeemed" : "Not Redeemed"}
                    </Button>
                    <Button variant="outline-secondary" onClick={cancelRedeemedChange}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {deletemodal && <DeleteConfirmationModal
                deleteModalShow={deletemodal}
                setDeleteModalShow={setDeleteModal}
                handleDeleteYes={handleDelete}
                name={`${selectedpromoCode?.code} ${isVoucherTab ? 'Voucher code' : 'Promo code'}`} />}
        </>
    );
};

export default PromoCodesTable;
